import React, { useState, useEffect } from "react";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import { inputStyle } from "components/StyledComponents/styledComponents";
import { StyledButton } from "components/StyledComponents/styledComponents";
import { StyledSnackbar, StyledAlert } from "components/StyledComponents/styledComponents.js";
import URLs from "constants/urls";
import ExpenditureIncomeReport from "./expenditureIncomeReport";
import { FaGrinAlt } from "react-icons/fa";
import BusinessBreakdownDialog from "./bussinessBreakdown.js";
import ConnectorPaymentsDialog from "./connectorPaymentsDialog.js";
import AdditionalPayoutsDialog from "./AdditionalPayoutsDialog.js";
import RecoveriesDialog from "./RecoveriesDailog.js";
import BalanceSheet from "./accountsReportsDashboard";
import { CustomPaper } from "components/Styles/globalStyles";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DmaPayoutsDialog from "./dmaPayoutsDialog";

const FinancialSummary = ({ isKh = false }) => {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const jwt_token = Cookie.get("jwt_token");
  const [openC, setOpenC] = useState(false);
  const [openB, setOpenB] = useState(false);
  const [openD, setOpenD] = useState(false);
  const theme = useTheme();
  const [selectedDsaId, setSelectedDsaId] = useState(0);
  const [dsaData, setDsaData] = useState([]);
  const isMobile = useMediaQuery("(max-width:600px)");
  const MInput = inputStyle();
  const [dmaPayoutData, setDmaPayoutData] = useState([]);
  const [dmaPayoutLoading, setDmaPayoutLoading] = useState(false);
  const [dmaPayoutError, setDmaPayoutError] = useState(null);

  useEffect(() => {
    fetchDsaMasterData();
  }, []);

  const fetchDsaMasterData = async () => {
    const url = URLs.hrmsDsaLoginDetailsUrl;
    const options = {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    };
    try {
      const response = await fetch(url, options);
      const data = await response.json();
      const sortedData = data.sort((a, b) => a.dsaName.localeCompare(b.dsaName));
      setDsaData(sortedData);
    } catch (error) {
      console.log("Failed to fetch company data", "error");
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue) {
      setSelectedDsaId(newValue.dsaId);
    } else {
      setSelectedDsaId(null);
    }
  };

  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));

  const [summary, setSummary] = useState({
    expenseDetails: [],
    businessBreakdown: [],
    totalExpenses: { particulars: "", amount: "", month: "" },
    receivedRevenue: { particulars: "", amount: "", month: "" },
    tdsCalc: { particulars: "", amount: "" },
    netRevenue: { particulars: "", amount: "" },
    dmaPayouts: { particulars: "", amount: "" },
    estimatedNetProfit: { particulars: "", amount: "" },
  });

  const [summaryByMis, setSummaryByMis] = useState({
    expenseDetails: [],
    businessBreakdown: [],
    totalExpenses: { particulars: "", amount: "" },
    receivedRevenue: { particulars: "", amount: "" },
    totalExpectedRevenue: { particulars: "", amount: "" },
    totalReceivedRevenue: { particulars: "", amount: "" },
    notReceivedRevenue: { particulars: "", amount: "" },
    estimatedNetProfit: { particulars: "", amount: "" },
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const orange = "#F97316";
  const charcoal = "#1F2937";

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchSummary = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      if (user?.companyId === undefined) {
        setAlert("Please login first");
        return;
      }

      let url = "";

      if (selectedDsaId === 0) {
        url = !isKh
          ? `${URLs.newPLreportUrl}?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId}`
          : `${URLs.khActualPandLReport}?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId}`;
      } else {
        url = !isKh
          ? `${URLs.dmaEstimatedPandLReport}?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId}`
          : `${URLs.dmaActualPandLReport}?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch data");
      }

      const data = await response.json();

      const isKhAll = isKh && selectedDsaId === 0;

      // API1 (KH, companyId=0): dmaPayouts & connectorPayouts already INSIDE expenseDetails — do NOT re-add
      // API2 (DMA, companyId!=0): dmaPayouts & connectorPayouts are separate — merge into expenseDetails
      // const mergedExpenseDetails = isKhAll
      //   ? data.expenseDetails || []
      //   : [
      //       ...(data.expenseDetails || []),
      //       data.dmaPayouts || { month: "", particulars: "DMA Payouts", amount: 0 },
      //       data.connectorPayouts || { month: "", particulars: "Connector Payouts", amount: 0 },

      //     ];
      const mergedExpenseDetails = data.expenseDetails || [];

      const incomeTotal = (data.businessBreakdown || []).reduce(
        (sum, e) => sum + (e.amount || 0),
        0
      );
      const expenseTotal = mergedExpenseDetails.reduce((sum, e) => sum + (e.amount || 0), 0);

      let adjustedExpenseDetails = [...mergedExpenseDetails];
      let adjustedBusinessBreakdown = [...(data.businessBreakdown || [])];

      // Balance the ledger
      // if (expenseTotal > incomeTotal) {
      //   const diff = expenseTotal - incomeTotal;
      //   adjustedBusinessBreakdown.push({
      //     particulars: "Expenditure excess than Income (Loss)",
      //     amount: diff,
      //   });
      // } else if (incomeTotal > expenseTotal) {
      //   const diff = incomeTotal - expenseTotal;
      //   adjustedExpenseDetails.push({
      //     particulars: "Income excess than Expenditure (Profit)",
      //     amount: diff,
      //   });
      // }

      const finalExpenseTotal = adjustedExpenseDetails.reduce((sum, e) => sum + (e.amount || 0), 0);
      const finalIncomeTotal = adjustedBusinessBreakdown.reduce(
        (sum, e) => sum + (e.amount || 0),
        0
      );

      const finalFormattedData = {
        // ✅ spread raw data FIRST — preserves subtotalGrossRevenue, creditNotes, debitNotes, netAdjustedRevenue
        ...data,
        // Override only recalculated fields
        expenseDetails: adjustedExpenseDetails,
        businessBreakdown: adjustedBusinessBreakdown,
        totalExpenses: data.totalExpenses,
        receivedRevenue: { particulars: "Total Est Rev", amount: finalIncomeTotal },
        income: incomeTotal,
        // Explicitly preserve KH-only new fields for clarity
        subtotalGrossRevenue: data.subtotalGrossRevenue || null,
        creditNotes: data.creditNotes || null,
        debitNotes: data.debitNotes || null,
        netAdjustedRevenue: data.netAdjustedRevenue || null,
        estimatedNetProfit:
          data.estimatedNetProfit != null
            ? {
                particulars:
                  data.estimatedNetProfit.amount < 0
                    ? "Expenditure excess than Income (Loss)"
                    : "Income excess than Expenditure (Profit)",
                amount: Math.abs(data.estimatedNetProfit.amount),
              }
            : null,
      };

      // if (isKh) {
      //   setSummary(finalFormattedData);
      // } else {
      //   setSummary({
      //     ...data,
      //     expenseDetails: mergedExpenseDetails,
      //   });
      // }
      setSummary(finalFormattedData);
    } catch (error) {
      setAlert(`Failed to fetch financial summary: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryByMis = async () => {
    setLoading(true);
    setErrorMsg("");
    const companyId = selectedDsaId;
    try {
      let url = "";
      if (companyId === 0) {
        url = `${URLs.profitAndLossByMisDate}?startDate=${startDate}&endDate=${endDate}&companyId=${companyId}`;
      } else {
        url = `${URLs.dmaActualPandLReportByMisDate}?startDate=${startDate}&endDate=${endDate}&companyId=${companyId}`;
      }
      console.log("companyId", companyId);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to fetch summary");
      }

      const data = await response.json();

      const received = (data.businessBreakdown || []).filter(
        (item) => !item.particulars?.toLowerCase().includes("not received")
      );
      const notReceived = (data.businessBreakdown || []).filter((item) =>
        item.particulars?.toLowerCase().includes("not received")
      );

      const totalExpectedRevenue = [...received, ...notReceived].reduce(
        (sum, i) => sum + (i.amount || 0),
        0
      );
      const totalReceivedRevenue = received.reduce((sum, i) => sum + (i.amount || 0), 0);
      const notReceivedRevenue = notReceived.reduce((sum, i) => sum + (i.amount || 0), 0);

      const adjustedExpenseDetails = [
        ...(data.expenseDetails || []),
        data.dmaPayouts || { month: "", particulars: "DMA Payouts", amount: 0 },
        data.connectorPayouts || { month: "", particulars: "Connector Payouts", amount: 0 },
      ];

      let adjustedBusinessBreakdown = [...(received || [])];
      // const expenseTotal = data.totalExpenses?.amount || 0;
      // const incomeTotal = data.receivedRevenue?.amount || 0;

      // if (expenseTotal > incomeTotal) {
      //   const diff = expenseTotal - incomeTotal;
      //   adjustedBusinessBreakdown.push({
      //     particulars: `Expenditure excess than Income (Loss)`,
      //     amount: diff,
      //   });
      // } else if (incomeTotal > expenseTotal) {
      //   const diff = incomeTotal - expenseTotal;
      //   adjustedExpenseDetails.push({
      //     particulars: `Income excess than Expenditure (Profit)`,
      //     amount: diff,
      //   });
      // }

      const finalExpenseTotal = adjustedExpenseDetails.reduce((sum, e) => sum + (e.amount || 0), 0);
      const finalIncomeTotal = adjustedBusinessBreakdown.reduce(
        (sum, e) => sum + (e.amount || 0),
        0
      );

      setSummaryByMis({
        ...data,
        expenseDetails: adjustedExpenseDetails,
        receivedIncome: adjustedBusinessBreakdown,
        notReceivedIncome: notReceived,
        totalExpectedRevenue: {
          particulars: "Total Expected Revenue",
          amount: totalExpectedRevenue,
        },
        totalReceivedRevenue: {
          particulars: "Total Received Revenue",
          amount: totalReceivedRevenue,
        },
        finalIncomeTotal: {
          particulars: "Total Received Revenue",
          amount: finalIncomeTotal,
        },
        finalExpenseTotal: {
          particulars: "Total Expenses",
          amount: finalExpenseTotal,
        },
        notReceivedRevenue: { particulars: "Not Received Revenue", amount: notReceivedRevenue },

        receivedRevenue: data.receivedRevenue || null, // "Total Received Revenue" — SubTotal Gross Revenue
        additionalPayout: data.additionalPayout || null, // "Total Additional Payout" — Debit Notes
        recoveryAmounts: data.recoveryAmounts || null, // "Total Recovery" — Credit Notes (brackets)
        totalNetReceivedAmount: data.totalNetReceivedAmount || null, // "Total Net Received Amount" — NET ADJUSTED REVENUE
        estimatedNetProfit:
          data.estimatedNetProfit != null
            ? {
                particulars:
                  data.estimatedNetProfit.amount < 0
                    ? "Expenditure excess than Income (Loss)"
                    : "Income excess than Expenditure (Profit)",
                amount: Math.abs(data.estimatedNetProfit.amount),
              }
            : null,
        // ✅ pass through for expenditure footer
        dmaPayouts: data.dmaPayouts || null,
        connectorPayouts: data.connectorPayouts || null,
        totalExpenses: data.totalExpenses || null,
      });
    } catch (error) {
      setErrorMsg(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDmaPayoutsClick = async () => {
    setOpenD(true);
    setDmaPayoutLoading(true);
    setDmaPayoutError(null);

    try {
      const url =
        `${URLs.dmaPayoutBreakdown}` +
        `?startDate=${startDate}` +
        `&endDate=${endDate}` +
        `&companyId=${selectedDsaId ?? 0}`;

      console.log("➡️ DMA URL:", url);
      console.log("➡️ Token:", jwt_token);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      console.log("➡️ Response status:", response.status);

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      console.log("➡️ DMA Data received:", data);
      setDmaPayoutData(data || []);
    } catch (err) {
      console.log("➡️ Error:", err.message);
      setDmaPayoutError(err.message || "Failed to fetch DMA Payouts data");
    } finally {
      setDmaPayoutLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const apiUrl = `${URLs.khAllDisbursedBusinessUrl}?fromDate=${startDate}&toDate=${endDate}&assignCompanyId=${selectedDsaId}`;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();

      const wsData = [];

      wsData.push(["Business Entries"]);
      if (data.length > 0) {
        const columns = Object.keys(data[0]);
        wsData.push(columns);
        data.forEach((row) => wsData.push(columns.map((col) => row[col])));
      } else {
        wsData.push(["No business entries found"]);
      }
      wsData.push([]);

      wsData.push([` Revenue & Profit – ${summary.receivedRevenue?.month || "N/A"}`]);
      wsData.push(["Particulars", "Amount (₹)"]);
      summary.businessBreakdown.forEach((item) => {
        wsData.push([item.particulars, item.amount]);
      });

      const totals = [
        { label: "Total Estimated Revenue", value: summary.receivedRevenue?.amount || 0 },
        { label: "TDS Calculated", value: summary.tdsCalc?.amount || 0 },
        { label: "Net Revenue", value: summary.netRevenue?.amount || 0 },
        { label: "Total Expenses", value: summary.totalExpenses?.amount || 0 },
        { label: "Actual Net Profit", value: summary.estimatedNetProfit?.amount || 0 },
      ];
      wsData.push([]);
      totals.forEach((total) => wsData.push([total.label, total.value]));
      wsData.push([]);

      wsData.push([`Expenses – ${summary.totalExpenses?.month || "N/A"}`]);
      wsData.push(["Particulars", "Amount (₹)"]);
      summary.expenseDetails.forEach((item) => {
        wsData.push([item.particulars, item.amount]);
      });
      wsData.push([]);
      wsData.push(["Total Expenses", summary.totalExpenses?.amount || 0]);

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!cols"] = [{ wch: 40 }, { wch: 20 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Summary");
      XLSX.writeFile(workbook, `Financial_Summary_${startDate}_${endDate}.xlsx`);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 0, mt: 2 }}>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      {!isKh && (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, maxWidth: "100%" }}>
          <Typography variant="h6" fontWeight="bold" fontSize={{ xs: 16, sm: 18 }} gutterBottom>
            Financial Summary Report
          </Typography>

          <Grid
            container
            spacing={2}
            justifyContent="align-left"
            alignItems="center"
            sx={{ flexWrap: isMobile ? "wrap" : "nowrap", textAlign: "center", mt: 1 }}
          >
            <Grid item>
              <TextField
                label="Start Date"
                size="small"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputProps={{ sx: { fontSize: 14 } }}
                sx={{ ...MInput.b, maxWidth: 200, width: "100%" }}
                InputLabelProps={{ sx: { color: MInput.a } }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="End Date"
                size="small"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputProps={{ sx: { fontSize: 14 } }}
                sx={{ ...MInput.b, maxWidth: 200, width: "100%" }}
                InputLabelProps={{ sx: { color: MInput.a } }}
              />
            </Grid>
            <Grid item>
              <Box sx={{ maxWidth: 200, width: "100%" }}>
                <StyledButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    fetchSummary();
                    fetchSummaryByMis();
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Generate Report"}
                </StyledButton>
              </Box>
            </Grid>
            <Grid item>
              <StyledButton
                fullWidth
                variant="contained"
                color="secondary"
                onClick={downloadExcel}
                disabled={!summary || summary.expenseDetails?.length === 0}
              >
                Download Excel
              </StyledButton>
            </Grid>
          </Grid>

          {errorMsg && (
            <Box mt={2}>
              <Alert severity="error">{errorMsg}</Alert>
            </Box>
          )}

          {!loading && summary.length === 0 && (
            <Typography mt={3} fontSize={14} color="text.secondary">
              No data found for the selected date range.
            </Typography>
          )}

          {summary &&
            Array.isArray(summary.expenseDetails) &&
            summary.expenseDetails.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 2, px: 0 }}>
                {/* Left Side – Expenses */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      p: 2,
                      transition: "transform 0.2s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    <Typography sx={{ fontWeight: "bold", fontSize: 18, color: charcoal, mb: 1 }}>
                      Expenses – {summary.totalExpenses?.month || "Month"}
                    </Typography>
                    <Divider sx={{ my: 1, backgroundColor: orange, height: 2 }} />
                    {summary.expenseDetails.map((item, index) => (
                      <Grid
                        container
                        key={index}
                        justifyContent="space-between"
                        sx={{
                          fontSize: 14,
                          mb: 0.5,
                          color: charcoal,
                          py: 0.5,
                          "&:hover": { backgroundColor: "#FFF7ED" },
                        }}
                      >
                        <Grid item>{item.particulars}</Grid>
                        <Grid item>
                          ₹{" "}
                          {Number(item.amount).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </Grid>
                      </Grid>
                    ))}
                    <Box mt={2} pt={1} borderTop={`1px solid ${orange}`}>
                      <Grid container justifyContent="space-between">
                        <Typography sx={{ fontSize: 16, fontWeight: "bold", color: charcoal }}>
                          Total Expenses
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: "bold", color: orange }}>
                          ₹ {Number(summary.totalExpenses?.amount || 0).toLocaleString("en-IN")}
                        </Typography>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>

                {/* Right Side – Revenue & Profit */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      p: 2,
                      transition: "transform 0.2s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    <Typography sx={{ fontWeight: "bold", fontSize: 18, color: charcoal, mb: 1 }}>
                      Revenue – {summary.receivedRevenue?.month || "Month"}
                    </Typography>
                    <Divider sx={{ my: 1, backgroundColor: orange, height: 2 }} />
                    {Array.isArray(summary.businessBreakdown) &&
                      summary.businessBreakdown.map((item, index) => (
                        <Grid
                          container
                          key={index}
                          justifyContent="space-between"
                          sx={{
                            fontSize: 14,
                            mb: 0.5,
                            color: charcoal,
                            py: 0.5,
                            pl: "10px",
                            pr: "10px",
                            "&:hover": { backgroundColor: "#FFF7ED" },
                          }}
                        >
                          <Grid item>{item.particulars}</Grid>
                          <Grid item>
                            ₹{" "}
                            {Number(item.amount).toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </Grid>
                        </Grid>
                      ))}
                    <Divider sx={{ my: 1, backgroundColor: orange, height: 2 }} />
                    {[
                      "receivedRevenue",
                      "tdsCalc",
                      "netRevenue",
                      "totalExpenses",
                      "estimatedNetProfit",
                    ].map((key) => {
                      const section = summary?.[key];
                      if (!section) return null;
                      return (
                        <Grid
                          container
                          key={key}
                          justifyContent="space-between"
                          sx={{
                            py: 0.5,
                            backgroundColor:
                              key === "estimatedNetProfit"
                                ? section.amount < 0
                                  ? "red"
                                  : "#ffffedff"
                                : "transparent",
                            borderRadius: "4px",
                            pl: "10px",
                            pr: "10px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: key === "estimatedNetProfit" ? "bold" : "normal",
                              color:
                                key === "estimatedNetProfit" && section.amount < 0
                                  ? "white"
                                  : charcoal,
                            }}
                          >
                            {key === "estimatedNetProfit"
                              ? "Actual Net Profit"
                              : section.particulars}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: key === "estimatedNetProfit" ? "bold" : "normal",
                              color:
                                key === "estimatedNetProfit" && section.amount < 0
                                  ? "white"
                                  : key === "estimatedNetProfit"
                                  ? orange
                                  : charcoal,
                            }}
                          >
                            ₹ {Number(section.amount || 0).toLocaleString("en-IN")}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
            )}
        </Paper>
      )}

      {isKh && (
        <>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 2,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              maxWidth: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ flexWrap: isMobile ? "wrap" : "nowrap" }}
            >
              <Grid item>
                <TextField
                  label="Start Date"
                  size="small"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputProps={{ sx: { fontSize: 14 } }}
                  sx={{ maxWidth: 200, width: "100%" }}
                  InputLabelProps={{ sx: { color: MInput.a } }}
                />
              </Grid>
              <Grid item>
                <TextField
                  label="End Date"
                  size="small"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputProps={{ sx: { fontSize: 14 } }}
                  sx={{ maxWidth: 200, width: "100%" }}
                  InputLabelProps={{ sx: { color: MInput.a } }}
                />
              </Grid>
              <Grid item>
                <Grid item>
                  <Autocomplete
                    options={dsaData}
                    getOptionLabel={(option) => `${option.dsaName} (${option.dsaCode})`}
                    value={dsaData.find((item) => item.dsaId === selectedDsaId) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedDsaId(newValue.dsaId);
                      } else {
                        setSelectedDsaId(0);
                      }
                    }}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select DSA"
                        variant="outlined"
                        sx={{
                          height: 40,
                          "& .MuiInputBase-root": {
                            height: "100%",
                            minWidth: 200,
                            width: "100%",
                            minHeight: 35,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item>
                <StyledButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    fetchSummary();
                    fetchSummaryByMis();
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Generate Report"}
                </StyledButton>
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                backgroundColor: "#333333",
                p: 1,
                borderRadius: 2,
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#333333",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#555555" },
                }}
                onClick={() => {
                  setOpenB(false);
                  setTimeout(() => setOpenB(true), 0);
                }}
              >
                Show Business Breakdown
              </Button>
              <BusinessBreakdownDialog
                open={openB}
                onClose={() => setOpenB(false)}
                startDate={startDate}
                endDate={endDate}
                dsaId={user?.companyId || 0}
                user={{ ...user, companyId: selectedDsaId ?? 0 }}
                setAlert={setAlert}
                isIncomeByMis={false}
                urlApi={
                  selectedDsaId === 0 ? URLs.businessBreakdownUrl : URLs.dsaBusinessBreakdownUrl
                }
                selectedDsaId={selectedDsaId}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#333333",
                  color: "#fff",
                  ml: 2,
                  "&:hover": { backgroundColor: "#555555" },
                }}
                onClick={handleDmaPayoutsClick}
              >
                Show DMA Payouts
              </Button>
              <DmaPayoutsDialog
                open={openD}
                onClose={() => {
                  setOpenD(false);
                  setDmaPayoutData([]);
                  setDmaPayoutError(null);
                }}
                startDate={startDate}
                endDate={endDate}
                data={dmaPayoutData}
                loading={dmaPayoutLoading}
                error={dmaPayoutError}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#333333",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#555555" },
                }}
                onClick={() => setOpenC(true)}
              >
                Show Connector Payments
              </Button>
              <ConnectorPaymentsDialog
                open={openC}
                onClose={() => setOpenC(false)}
                startDate={startDate}
                endDate={endDate}
                dsaId={selectedDsaId}
                user={{ ...user, companyId: selectedDsaId }}
              />
            </Box>
          </Paper>

          <ExpenditureIncomeReport
            data={summary}
            isIncomeByMis={false}
            startDate={startDate}
            endDate={endDate}
            setAlert={setAlert}
            selectedDsaId={selectedDsaId}
            onConnectorClick={() => setOpenC(true)}
          />
          <ExpenditureIncomeReport
            data={summaryByMis}
            isIncomeByMis={true}
            startDate={startDate}
            endDate={endDate}
            setAlert={setAlert}
            selectedDsaId={selectedDsaId}
            onConnectorClick={() => setOpenC(true)}
          />
          <br />
          <BalanceSheet startDate={startDate} endDate={endDate} selectedDsaId={selectedDsaId} />
        </>
      )}
    </Box>
  );
};

FinancialSummary.propTypes = {
  isKh: PropTypes.bool,
};

export default FinancialSummary;
